import React, { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { createEventId, trackEvent } from "../utils/tracking"

const PopupGate = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [showPopup, setShowPopup] = useState(false)
  const [agree, setAgree] = useState(false)
  const [ageConfirmed, setAgeConfirmed] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)

  const proceedToNavigation = useCallback((navigation) => {
    if (!navigation) return

    const { destination, affiliateEventParams } = navigation
    if (!destination) return

    setShowPopup(false)
    setPendingNavigation(null)

    if (destination.origin === window.location.origin) {
      navigate(destination.pathname + destination.search + destination.hash)
      return
    }

    if (affiliateEventParams) {
      try {
        const eventId = createEventId("AffiliateOutboundVisit")
        trackEvent("AffiliateOutboundVisit", {
          ...affiliateEventParams,
          url: destination.href,
        }, { eventId })
      } catch (err) {
        // Tracking should never block navigation.
      }
    }

    window.open(destination.href, "_blank", "noopener,noreferrer")
  }, [navigate])

  useEffect(() => {
    const shouldSkipPopup = ["/terms-of-service", "/not-available"].includes(location.pathname)
    if (shouldSkipPopup) {
      setShowPopup(false)
      setPendingNavigation(null)
    }
  }, [location.pathname])

  useEffect(() => {
    const handleClick = (event) => {
      const target = event.target
      if (!(target instanceof Element)) return

      const anchor = target.closest("a")
      if (!anchor || anchor.dataset.popupGateIgnore === "true") return
      if (anchor.dataset.popupGate !== "true") return
      if (anchor.hasAttribute("download")) return

      const href = anchor.getAttribute("href")
      if (!href || href.startsWith("#")) return

      let destination
      try {
        destination = new URL(href, window.location.href)
      } catch {
        return
      }

      const sameLocation =
        destination.origin === window.location.origin &&
        destination.pathname === window.location.pathname &&
        destination.search === window.location.search &&
        destination.hash === window.location.hash

      if (sameLocation) return

      const isAffiliateOutbound = anchor.dataset.affiliateOutbound === "true"
      const navigation = {
        destination,
        affiliateEventParams: isAffiliateOutbound
          ? {
              website: anchor.dataset.affiliateWebsite || undefined,
              code: anchor.dataset.affiliateCode || undefined,
            }
          : null,
      }

      event.preventDefault()
      event.stopPropagation()

      const lastPopupTime = localStorage.getItem("lastPopupTime")
      const timeElapsed = lastPopupTime ? Date.now() - parseInt(lastPopupTime, 10) : Infinity
      const cooldown = 604800 * 1000

      if (timeElapsed < cooldown) {
        proceedToNavigation(navigation)
        return
      }

      setPendingNavigation(navigation)
      setShowPopup(true)
      setAgree(false)
      setAgeConfirmed(false)
    }

    // Use the bubble phase so React onClick tracking runs before navigation is gated.
    document.addEventListener("click", handleClick)
    return () => document.removeEventListener("click", handleClick)
  }, [location.pathname, proceedToNavigation])

  const handleConfirm = () => {
    if (!(agree && ageConfirmed)) return

    localStorage.setItem("lastPopupTime", Date.now().toString())
    proceedToNavigation(pendingNavigation)
  }

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      setShowPopup(false)
      setPendingNavigation(null)
    }
  }

  return (
    <>
      {children}

      {showPopup &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex justify-center items-center"
            onClick={handleOverlayClick}
          >
            <div className="bg-[#0F1215] border border-[#2A2D31] shadow-[0_0_15px_rgba(255,107,0,0.3)] rounded-2xl p-6 w-full max-w-md text-white" onClick={(event) => event.stopPropagation()}>
              <h3 className="text-2xl font-bold font-display mb-4 text-center">One Last Step</h3>
              <p className="text-blue-100/80 text-sm text-center mb-6">
                You are about to leave CaseRTP for another website. You must be 18+ to continue.
              </p>

              <div className="space-y-4 text-sm text-blue-100/80">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                    className="accent-[#FF6B00] w-4 h-4 rounded-sm"
                  />
                  I agree to the
                  <Link
                    to="/terms-of-service"
                    data-popup-gate-ignore="true"
                    className="text-[#FF6B00] underline hover:text-[#FF9A3D]"
                  >
                    Terms of Service
                  </Link>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ageConfirmed}
                    onChange={(e) => setAgeConfirmed(e.target.checked)}
                    className="accent-[#FF6B00] w-4 h-4 rounded-sm"
                  />
                  I confirm that I'm 18+ years old
                </label>
              </div>

              <button
                onClick={handleConfirm}
                disabled={!(agree && ageConfirmed)}
                className={`mt-6 w-full py-2 px-4 rounded-lg font-semibold transition-all duration-300 ${
                  agree && ageConfirmed
                    ? "bg-gradient-to-r from-[#FF6B00] to-[#FF9A3D] text-white hover:opacity-90 shadow-[0_0_12px_rgba(255,107,0,0.5)]"
                    : "bg-[#2A2D31] text-gray-400 cursor-not-allowed"
                }`}
              >
                Continue
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export default PopupGate
